<template>
  <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onSubmit">
    <v-text-field
      v-model="form.name"
      :rules="rules.name"
      label="Название информера"
    />

    <custom-select
      class="mt-2"
      :list="regionsList"
      :data="data.regionId"
      :search-is-loading="isLoading"
      label="Выберите регион"
      search-label="Поиск региона"
      search
      item-value="value"
      item-text="text"
      :rules="rules.regionId"
      @change="onChangeRegion"
      @search="debouncedRegionSearch"
    />

    <div v-if="form.regionId">
      <h4 class="mb-2">
        Установите информеры внутри региона
        {{ pointMarkersCount }}/{{ MAX_POINT_MARKERS }})
      </h4>
      <div class="dialog-container">
        <map-component @created="onMapCreated" />
      </div>
    </div>
    <v-btn
      class="primary float-right mt-2"
      @click="onSubmit"
    >
      Сохранить
    </v-btn>
  </v-form>
</template>

<script>
import CustomSelect from '@/components/ui/custom-select';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { mapActions, mapState } from 'vuex';
import MapComponent from '@/components/map';
import Mapbox, { LngLat } from 'mapbox-gl';

const MAX_POINT_MARKERS = 20;

const createDefaultForm = () => ({
  regionId: null,
  name: '',
});

const createDefaultRules = () => ({
  regionId: [v => !!v || 'Выберите регион'],
  name: [v => !!v || 'Введите название информера'],
});

export default {
  name: 'dangerous-informers-form',
  components: { CustomSelect, MapComponent },
  props: {
    isEdit: Boolean,
    data: {
      type: Object,
      default: createDefaultForm,
    },
    dialogIsOpen: Boolean,
    isFullscreen: Boolean,
  },
  data: () => ({
    form: createDefaultForm(),
    rules: createDefaultRules(),
    isLoading: false,
    pointMarkersCount: 0,
    valid: true,
    onPopupOpen: false,
  }),
  computed: {
    ...mapState('filters', {
      regionsList: 'availableRegions',
    }),
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(value) {
        this.form = {
          ...this.form,
          ...JSON.parse(JSON.stringify(value)),
        };
      },
    },
    dialogIsOpen(state) {
      if (state) {
        this.mapResize();
      }
    },
    isFullscreen() {
      setTimeout(this.mapResize.bind(this), 500);
    },

  },
  created() {
    this.form = {
      ...this.form,
      ...JSON.parse(JSON.stringify(this.data)),
    };

    this.MAX_POINT_MARKERS = MAX_POINT_MARKERS;
    this.debouncedRegionSearch = debounce(this.onRegionSearch, 1000);
  },
  methods: {
    ...mapActions('filters', {
      searchRegions: 'getAvailable',
    }),

    ...mapActions('regions', {
      regionGetBbox: 'getBbox',
      regionGetBounds: 'getBounds',
    }),

    mapResize() {
      if (this.mapInstance) this.mapInstance.resize();
    },

    initData() {
      if (this.mapInstance) {
        this.removeAllPoints();

        this.drawBounds();

        if (this.data.points) {
          this.data.points.forEach(_ => {
            this.addMarker((new LngLat(_.lng, _.lat)), this.mapInstance);
          });
        }

        this.mapInstance.fitBounds(this.bbox);
      }
    },

    drawBounds() {
      if (this.bounds) {
        if (this.mapInstance.getLayer('region-bounds-layer')) {
          this.mapInstance.removeLayer('region-bounds-layer');
        }

        if (this.mapInstance.getSource('region-bounds-source')) {
          this.mapInstance.removeSource('region-bounds-source');
        }

        this.boundsLayer = {
          id: 'region-bounds-layer',
          type: 'line',
          source: 'region-bounds-source',
          paint: {
            'line-color': 'rgba(255,0,136,0.41)',
            'line-width': 2,
          },
        };
        this.mapInstance.addSource('region-bounds-source', {
          type: 'geojson',
          data: this.bounds,
        });
        this.mapInstance.addLayer(this.boundsLayer);
      }
    },

    async onChangeRegion(data) {
      if (typeof data === 'string') {
        this.form.regionId = data;
      } else {
        this.form.regionId = get(data, 'id', null);
      }

      this.bounds = await this.regionGetBounds([this.form.regionId]);

      if (this.form.regionId) {
        this.bbox = await this.regionGetBbox(this.form.regionId);

        this.initData();
      }
    },

    removeMarker(id) {
      const index = this.pointMarkers.findIndex(_ => _.id === id);

      if (index === -1) return;

      this.pointMarkers[index].marker.remove();
      delete this.pointMarkers[index].marker;

      this.pointMarkers.splice(index, 1);

      this.pointMarkersCount -= 1;
    },

    removeAllPoints() {
      const markers = [...this.pointMarkers];
      markers.forEach(_ => this.removeMarker(_.id));
    },

    onMapCreated(instance) {
      this.mapInstance = instance;

      this.pointMarkers = [];

      if (this.data.points) {
        this.data.points.forEach(_ => {
          this.addMarker((new LngLat(_.lng, _.lat)), instance);
        });
      }

      // eslint-disable-next-line no-param-reassign
      instance.getCanvas().style.cursor = 'crosshair';

      instance.on('click', (event) => this.onMapClick(event, instance));
    },

    onMapClick(event, instance) {
      this.$nextTick(() => {
        if (!this.onPopupOpen) this.addMarker(event.lngLat, instance);
      });
    },

    addMarker(lngLat, instance) {
      if (this.pointMarkers.length >= MAX_POINT_MARKERS) {
        this.pointMarkers[0].marker.remove();
        delete this.pointMarkers[0].marker;
        this.pointMarkers.shift();
      }

      const n = Math.floor(Math.random() * 11);
      const k = Math.floor(Math.random() * 1000000);
      const id = n + k;

      const el = document.createElement('div');
      const a = document.createElement('a');
      a.innerText = 'Удалить';
      a.id = `marker-remove-${id}`;
      a.href = '#';
      el.appendChild(a);

      const popup = new Mapbox.Popup({ offset: 25 })
        .setHTML(el.innerHTML);

      popup.on('open', () => {
        this.onPopupOpen = true;
        const link = document.getElementById(`marker-remove-${id}`);
        link.onclick = () => this.removeMarker(id);
      });

      popup.on('close', () => {
        this.onPopupOpen = false;
      });

      const marker = new Mapbox.Marker()
        .setLngLat(lngLat)
        .setPopup(popup)
        .addTo(instance);

      this.pointMarkers.push({
        id,
        marker,
        coords: lngLat.toArray(),
      });

      this.pointMarkersCount = this.pointMarkers.length;
    },

    async onRegionSearch(query) {
      await this.searchRegions({ adminLevel: 4, query });
    },

    onSubmit() {
      if (this.$refs.form.validate()) {
        this.$emit('submit', {
          form: this.form,
          points: this.pointMarkers.map(_ => _.coords),
          isEdit: this.isEdit,
        });
        this.data.regionId = null;
        this.form = createDefaultForm();
        this.removeAllPoints();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .dialog-container {
    position: relative;
    height: 450px;
    width: 100%;
  }

  .v-dialog--fullscreen {
    .dialog-container {
      height: 600px !important;
    }
  }
</style>
